import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Card, CardContent,
  Table, TableHead, TableBody, TableRow, TableCell,
  Chip, IconButton, Alert, CircularProgress,
  Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import {
  Add, CheckCircle, Cancel, Warehouse, Edit, Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const statusColors: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info' | 'primary'> = {
  quarantine: 'warning',
  approved: 'success',
  rejected: 'error',
  in_silo: 'info',
  depleted: 'default',
};

const GrainLotList: React.FC = () => {
  const navigate = useNavigate();
  const [lots, setLots] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; id: string }>({ open: false, id: '' });
  const [rejectNotes, setRejectNotes] = useState('');
  const [siloDialog, setSiloDialog] = useState<{ open: boolean; id: string }>({ open: false, id: '' });
  const [silos, setSilos] = useState<any[]>([]);
  const [selectedSilo, setSelectedSilo] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [lotsRes, silosRes] = await Promise.all([
        millingApi.getGrainLots(statusFilter ? { status: statusFilter } : {}),
        millingApi.getSilos().catch(() => []),
      ]);
      setLots(lotsRes.data || lotsRes || []);
      setSilos(Array.isArray(silosRes) ? silosRes : []);
    } catch (e: any) {
      setError(e.message || 'Failed to load grain lots');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const handleApprove = async (id: string) => {
    await millingApi.approveGrainLot(id, 'admin');
    load();
  };

  const handleReject = async () => {
    await millingApi.rejectGrainLot(rejectDialog.id, rejectNotes);
    setRejectDialog({ open: false, id: '' });
    setRejectNotes('');
    load();
  };

  const handleAssignSilo = async () => {
    const silo = silos.find((s: any) => s.id === selectedSilo);
    if (!silo) return;
    await millingApi.assignGrainLotToSilo(siloDialog.id, silo.id, silo.siloCode);
    setSiloDialog({ open: false, id: '' });
    setSelectedSilo('');
    load();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Grain Lots</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={load}><Refresh /></IconButton>
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/milling/grain-lots/new')}>
            Receive Grain
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select value={statusFilter} label="Status Filter" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="quarantine">Quarantine</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="in_silo">In Silo</MenuItem>
              <MenuItem value="depleted">Depleted</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Lot Number</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Type / Variety</TableCell>
                <TableCell>Received (MT)</TableCell>
                <TableCell>Available (MT)</TableCell>
                <TableCell>Moisture%</TableCell>
                <TableCell>Protein%</TableCell>
                <TableCell>Aflatoxin (ppb)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lots.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">No grain lots found</TableCell>
                </TableRow>
              ) : lots.map((lot: any) => (
                <TableRow key={lot.id} hover>
                  <TableCell>
                    <Typography fontWeight="bold" variant="body2">{lot.lotNumber}</Typography>
                    <Typography variant="caption" color="textSecondary">{lot.receivedDate}</Typography>
                  </TableCell>
                  <TableCell>
                    {lot.supplierName}
                    {lot.countryOfOrigin && <Typography variant="caption" display="block" color="textSecondary">{lot.countryOfOrigin}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" textTransform="uppercase">{lot.grainType}</Typography>
                    {lot.grainVariety && <Chip label={lot.grainVariety.toUpperCase()} size="small" variant="outlined" />}
                  </TableCell>
                  <TableCell>{Number(lot.receivedQtyMt).toFixed(3)}</TableCell>
                  <TableCell>{Number(lot.availableQtyMt).toFixed(3)}</TableCell>
                  <TableCell>{lot.moisturePercent != null ? `${lot.moisturePercent}%` : '—'}</TableCell>
                  <TableCell>{lot.proteinPercent != null ? `${lot.proteinPercent}%` : '—'}</TableCell>
                  <TableCell>
                    {lot.aflatoxinPpb != null ? (
                      <Chip
                        label={`${lot.aflatoxinPpb} ppb`}
                        size="small"
                        color={Number(lot.aflatoxinPpb) > 10 ? 'error' : 'success'}
                      />
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip label={lot.status.replace('_', ' ')} color={statusColors[lot.status] || 'default'} size="small" />
                    {lot.siloCode && <Typography variant="caption" display="block">Silo: {lot.siloCode}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => navigate(`/milling/grain-lots/${lot.id}/edit`)} title="Edit">
                        <Edit fontSize="small" />
                      </IconButton>
                      {lot.status === 'quarantine' && (
                        <>
                          <IconButton size="small" color="success" onClick={() => handleApprove(lot.id)} title="Approve">
                            <CheckCircle fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => setRejectDialog({ open: true, id: lot.id })} title="Reject">
                            <Cancel fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {lot.status === 'approved' && (
                        <IconButton size="small" color="primary" onClick={() => setSiloDialog({ open: true, id: lot.id })} title="Assign to Silo">
                          <Warehouse fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, id: '' })}>
        <DialogTitle>Reject Grain Lot</DialogTitle>
        <DialogContent>
          <TextField
            label="Rejection Notes"
            multiline rows={3} fullWidth
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, id: '' })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Silo Dialog */}
      <Dialog open={siloDialog.open} onClose={() => setSiloDialog({ open: false, id: '' })}>
        <DialogTitle>Assign to Silo</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Select Silo</InputLabel>
            <Select value={selectedSilo} label="Select Silo" onChange={(e) => setSelectedSilo(e.target.value)}>
              {silos.map((s: any) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.siloCode} — {s.siloName} ({Number(s.currentStockMt).toFixed(1)}/{Number(s.capacityMt).toFixed(1)} MT)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSiloDialog({ open: false, id: '' })}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignSilo} disabled={!selectedSilo}>Assign</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GrainLotList;
